import type { DecodedAudioChunk } from "./messages";

export function createAudioPlayback(context: AudioContext, destination: AudioNode) {
  type AudioEntry = {
    timestamp: number;
    buffer: AudioBuffer;
    source: AudioBufferSourceNode | null;
  };
  let entries: AudioEntry[] = [];
  let started = false;
  let position = 0;
  let clockStarted = 0;

  function stop(entry: AudioEntry) {
    if (!entry.source) return;
    entry.source.onended = null;
    entry.source.stop();
    entry.source.disconnect();
    entry.source = null;
  }

  function schedule(entry: AudioEntry) {
    if (!started || entry.source) return;
    const time = position + Math.max(0, context.currentTime - clockStarted);
    const offset = Math.max(0, time - entry.timestamp);
    if (offset >= entry.buffer.duration) return;
    const source = context.createBufferSource();
    source.buffer = entry.buffer;
    source.connect(destination);
    source.onended = () => {
      source.disconnect();
      entry.source = null;
    };
    source.start(
      Math.max(context.currentTime, clockStarted + entry.timestamp - position + offset),
      offset,
    );
    entry.source = source;
  }

  return {
    bufferedUntil(fallback: number) {
      const last = entries.at(-1);
      return last ? last.timestamp + last.buffer.duration : fallback;
    },
    discardBefore(seconds: number) {
      let discarded = 0;
      while (entries[0] && entries[0].timestamp + entries[0].buffer.duration <= seconds) {
        const entry = entries.shift();
        if (entry) stop(entry);
        discarded++;
      }
      return discarded;
    },
    enqueue(chunk: DecodedAudioChunk) {
      const length = chunk.channels[0]?.length ?? 0;
      if (!length) return;
      const buffer = context.createBuffer(chunk.channels.length, length, 48000);
      chunk.channels.forEach((channel, index) => {
        buffer.copyToChannel(channel, index);
      });
      const entry = { timestamp: chunk.timestamp, buffer, source: null };
      entries.push(entry);
      schedule(entry);
    },
    start(seconds: number, startTime: number) {
      position = seconds;
      clockStarted = startTime;
      started = true;
      for (const entry of entries) schedule(entry);
    },
    pause() {
      started = false;
      for (const entry of entries) stop(entry);
    },
    reset() {
      started = false;
      for (const entry of entries) stop(entry);
      entries = [];
    },
  };
}
